import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  CircleNotch,
  CheckCircle,
  XCircle,
  Prohibit,
  Lightning,
  Clock,
  MagnifyingGlass,
  ChatCircle,
  X,
  Stop,
  Code,
} from '@phosphor-icons/react';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { originToReturnTo } from '~/lib/chat-origin';
import {
  useBackgroundTasks,
  useSkillRunStatus,
  type ActiveTask,
  type FinishedTask,
} from '~/hooks/queries/use-inspector';
import { useResearchByRunId, useResearchCitations } from '~/hooks/queries/use-research';
import { useBackgroundChats, type BackgroundChat } from '~/hooks/queries/use-background-chats';
import { useChatStatuses } from '~/hooks/queries/use-chat-status';
import { ChatLight, type ChatLightState } from '~/features/chat/chat-light';
import { useDismissedSkillRuns } from '~/lib/stores/dismissed-skill-runs-store';
import { useDetachedBgChats } from '~/lib/stores/detached-bg-chats-store';
import {
  usePinnedSkillRuns,
  type PinnedSkillRun,
} from '~/lib/stores/pinned-skill-runs-store';
import { SkillFailureDialog } from './skill-failure-dialog';
import { SkillResultDialog } from './skill-result-dialog';
import { SkillProgressDialog } from './skill-progress-dialog';
import { useCancelBackgroundChat } from '~/hooks/mutations/use-cancel-background-chat';
import { useDismissChatWaiting } from '~/hooks/mutations/use-dismiss-chat-waiting';
import { useCancelResearch } from '~/hooks/mutations/use-cancel-research';
import { useSecondTicker } from '~/hooks/use-second-ticker';
import { formatElapsed } from '~/lib/elapsed';
import { Tip } from '~/components/ui/tip';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { llmKeys } from '~/lib/query-keys';
import { modelHeadline } from '~/components/model-badges';
import type { ModelsResponse } from '~/components/model-combobox';
import { skillRunModelLine } from './skill-run-model-line';

/**
 * FRONT-634 — the Inspector's live "Now" panel. Launchpad model (Mattias's
 * direct decision, 2026-07-03): a chat that launches a deep research is DONE
 * the moment the run detaches — the research becomes its own first-class run
 * here, with its own card/progress/Stop, delivering to the inbox and Files;
 * it has nothing further to do with the chat. What's shown:
 *   1. MONITORED (backgrounded) chats — a short list, one row per PROMOTED
 *      session (an ordinary chat closed mid-turn), a status dot from the
 *      shared chat-status spine (`useChatStatuses`), the title, and a
 *      relative time. Clicking a row reopens the real conversation (via
 *      `chat-open-store`).
 *   2. Coding-session cards — their own surface, unrelated to chats.
 *   3. Scheduled-job (cron) cards — display-only, link through to `/crons`.
 *   4. Skill / deep-research run cards (`SkillRunRow`) — the launchpad-model
 *      home for a detached deep research: live progress (phase, source
 *      count), a Stop button while running, and a persistent (dismissable)
 *      result/failure card once it settles, exactly as on main.
 * `GET /api/inspector/background-tasks` filters server-side to
 * `kind in ("coding", "cron", "skill")` — chat-initiated tool/delegate hops
 * are the one thing that stays permanently excluded (momentary orchestration
 * plumbing, never a standalone "background task" card).
 */

const PANEL_TASK_KINDS = ['coding', 'cron', 'skill'];

function relTime(ms: number): string {
  const d = Date.now() - ms;
  if (d < 1000) return 'now';
  if (d < 60_000) return `${Math.round(d / 1000)}s ago`;
  if (d < 3_600_000) return `${Math.round(d / 60_000)}m ago`;
  return `${Math.round(d / 3_600_000)}h ago`;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

/** A pinned (finished, kept) card carries enough to redraw itself; map it back
 *  to the ActiveTask shape SkillRunRow consumes. */
function pinnedToTask(c: PinnedSkillRun): ActiveTask {
  return {
    id: c.runId,
    kind: c.kind,
    name: c.name,
    sessionId: c.sessionId,
    startedAt: c.startedAt,
    runId: c.runId,
    skillSlug: c.skillSlug,
  };
}

/**
 * FRONT-745 — the active-tasks endpoint reads the "active" and "recent" sets
 * from two non-transactional queries, so a task can be reported in BOTH in
 * the same response (e.g. a tool call that finishes between the two reads).
 * Rendered as siblings, that produces a duplicate React key (and a confusing
 * double row). Drop anything from `tasks` whose id is already shown as
 * active — the active row is the authoritative one while it's still in that
 * set.
 */
export function excludeActiveIds<T extends { id: string }>(
  tasks: T[],
  active: { id: string }[],
): T[] {
  const activeIds = new Set(active.map((t) => t.id));
  return tasks.filter((t) => !activeIds.has(t.id));
}

/**
 * FRONT-707: one row per task id, first occurrence wins (the recent list is
 * newest-first). Coding task ids are the SESSION id, and a guided-build
 * session legitimately finishes several runs back-to-back (interview →
 * continuation → build) — without this the strip shows the same project
 * repeatedly and React warns about duplicate `recent-<id>` keys.
 */
export function dedupeById<T extends { id: string }>(tasks: T[]): T[] {
  const seen = new Set<string>();
  return tasks.filter((t) => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

/**
 * FRONT-634 — belt-and-suspenders: the endpoint already filters kinds
 * server-side, but the panel only ever wants to render `PANEL_TASK_KINDS`
 * here, so it re-asserts that instead of trusting every future caller of the
 * shared `ActiveTask`/`FinishedTask` shapes to keep the contract.
 */
export function onlyKinds<T extends { kind: string }>(tasks: T[], kinds: string[]): T[] {
  const allowed = new Set(kinds);
  return tasks.filter((t) => allowed.has(t.kind));
}

/**
 * FRONT-754 — a chat can have multiple turns (multiple `background_chats`
 * rows for the same session). Once a session is promoted, it earns exactly
 * ONE monitored-chat row, not one per turn — so collapse to each session's
 * most-recently-started row, which is also the one whose title/timestamp are
 * most representative of "what's this chat doing".
 */
export function latestBgChatPerSession(bgChats: BackgroundChat[]): Map<string, BackgroundChat> {
  const m = new Map<string, BackgroundChat>();
  for (const c of bgChats) {
    const existing = m.get(c.sessionId);
    if (!existing || Date.parse(c.startedAt) > Date.parse(existing.startedAt)) {
      m.set(c.sessionId, c);
    }
  }
  return m;
}

/**
 * FRONT-754 — which promoted chats to show as monitored-chat rows, and in
 * what order. A promoted (explicitly closed-while-busy) session shows
 * IMMEDIATELY — there is no minimum-alive bar anymore: promotion itself
 * (`detachedSessionIds`) is already the "this was genuinely detached, not a
 * quick Q&A" signal (decided at close time in `inbox-chat-modal.tsx`, which
 * promotes on EITHER a still-running bg-chat row OR a working/needs-you spine
 * status). The row then stays visible for as long as `chatStates` says the
 * session is `working` or `needs-you`. Once the spine goes terminal, the row
 * becomes a dismissable receipt; dismissing before then is a no-op via the
 * `isLive || !dismissed` gate below. Live rows sort first, then
 * newest-started. Under the launchpad model, a chat that only launched a
 * detached deep research never promotes at all — since FRONT-787 the spine
 * carries no research signal whatsoever — so this list is ordinary chats
 * closed mid-turn, not research runs (those are `SkillRunRow` below).
 */
export function selectVisibleBgChats(
  bgChats: BackgroundChat[],
  detachedSessionIds: Set<string>,
  dismissedSessionIds: Set<string>,
  chatStates: Map<string, ChatLightState>,
): BackgroundChat[] {
  const isLive = (sessionId: string): boolean => {
    const s = chatStates.get(sessionId);
    return s === 'working' || s === 'needs-you';
  };
  return [...latestBgChatPerSession(bgChats).values()]
    .filter((c) => detachedSessionIds.has(c.sessionId))
    .filter((c) => isLive(c.sessionId) || !dismissedSessionIds.has(c.sessionId))
    .sort((a, b) => {
      const aLive = isLive(a.sessionId);
      const bLive = isLive(b.sessionId);
      if (aLive && !bLive) return -1;
      if (bLive && !aLive) return 1;
      return Date.parse(b.startedAt) - Date.parse(a.startedAt);
    });
}

/**
 * FRONT-634/754 — one light-state per candidate (promoted) session, read
 * from the shared chat-status spine (`useChatStatuses`) so the dot matches
 * every other chat-status surface in the app (the launcher pills, the header
 * indicator) — and so the SAME state also drives `selectVisibleBgChats`'
 * liveness gate above (one signal, not two).
 *
 * Falls back to `working` for the brief window before the spine query has
 * resolved: a promoted session was captured because it was busy at close
 * time, so `working` is the safe default — a wrong `working` self-corrects
 * within a render once the query lands, whereas a wrong `done-seen` would
 * flash the row as an already-read receipt.
 *
 * EXCEPT when the session is already dismissed: falling back to `working`
 * there made `selectVisibleBgChats`'s `isLive || !dismissed` gate briefly
 * read `isLive = true` for a row the user had already dismissed, flashing it
 * back into view for one frame until the real (terminal) status loaded and
 * hid it again. A dismissed session's unresolved fallback is `done-seen`
 * instead — if the spine then reveals it's genuinely live after all (a new
 * turn re-armed it), the row correctly reappears once real data says so;
 * until then, it stays hidden like the user asked.
 */
export function bgChatLightStates(
  bgChats: BackgroundChat[],
  statuses: Record<string, { state: ChatLightState }> | undefined,
  dismissedSessionIds: Set<string>,
): Map<string, ChatLightState> {
  const m = new Map<string, ChatLightState>();
  for (const c of bgChats) {
    const known = statuses?.[c.sessionId]?.state;
    const fallback: ChatLightState = dismissedSessionIds.has(c.sessionId)
      ? 'done-seen'
      : 'working';
    m.set(c.sessionId, known ?? fallback);
  }
  return m;
}

export function BackgroundTasks() {
  const { data, isLoading, error } = useBackgroundTasks();
  // FRONT-634: this endpoint returns `coding`/`cron`/`skill` rows only
  // (filtered server-side in src/admin/inspector-routes.ts) — chat-initiated
  // tool/delegate hops never reach it.
  const active = onlyKinds(data?.active ?? [], PANEL_TASK_KINDS);
  const recent = onlyKinds(data?.recent ?? [], PANEL_TASK_KINDS);

  // FRONT-204/433/634/754: monitored (backgrounded) chats are their own
  // Postgres-backed data source (background_chats), collapsed to one row per
  // PROMOTED session (see selectVisibleBgChats / latestBgChatPerSession).
  const { data: bgChats } = useBackgroundChats();
  const dismissedIds = useDismissedSkillRuns((s) => s.ids); // shared: chat session ids AND skill-run ids
  const dismissPersistent = useDismissedSkillRuns((s) => s.dismiss);
  const detachedIds = useDetachedBgChats((s) => s.ids); // session ids (FRONT-754)
  const pinnedCards = usePinnedSkillRuns((s) => s.cards);
  const pin = usePinnedSkillRuns((s) => s.pin);
  const unpin = usePinnedSkillRuns((s) => s.unpin);

  // Persist every finished skill run the server reports (so the card survives a
  // refresh and stacks until dismissed). Seeding from `recent` captures the
  // authoritative success/failure; `pin` is a no-op for runs already kept, and
  // dismissed runs are never re-pinned.
  useEffect(() => {
    for (const t of recent) {
      if (t.kind !== 'skill' || !t.runId) continue;
      if (dismissedIds.has(t.runId)) continue;
      pin({
        runId: t.runId,
        kind: t.kind,
        name: t.name,
        sessionId: t.sessionId,
        skillSlug: t.skillSlug,
        startedAt: t.startedAt,
        finishedAt: t.finishedAt,
        isError: t.isError,
        cancelled: t.cancelled,
      });
    }
  }, [recent, dismissedIds, pin]);

  const dismissSkillRun = useCallback(
    (runId: string) => {
      // Record the dismissal (so the next server poll can't re-pin it) AND drop
      // it from the kept-cards store so the card leaves the panel immediately.
      dismissPersistent(runId);
      unpin(runId);
    },
    [dismissPersistent, unpin],
  );

  // The chat-status spine for every candidate (promoted) session — the
  // authoritative "is this session still genuinely live" signal, since it
  // folds in pending questions/approvals/plans, not just this session's own
  // (possibly already-settled) background_chats row.
  const candidateSessionIds = useMemo(() => {
    const ids = (bgChats ?? [])
      .filter((c) => detachedIds.has(c.sessionId))
      .map((c) => c.sessionId);
    return [...new Set(ids)];
  }, [bgChats, detachedIds]);
  const { data: statuses } = useChatStatuses(candidateSessionIds);
  const chatStates = bgChatLightStates(bgChats ?? [], statuses, dismissedIds);
  const bgVisible = selectVisibleBgChats(bgChats ?? [], detachedIds, dismissedIds, chatStates);

  if (isLoading && !data) {
    return <div className="text-xs text-muted-foreground/70 py-1">Loading…</div>;
  }
  if (error) {
    return (
      <div className="text-xs text-destructive">Failed: {(error as Error).message}</div>
    );
  }

  const activeRunIds = new Set(
    active.filter((t) => t.kind === 'skill' && t.runId).map((t) => t.runId!),
  );

  // Finished cards to show = persisted pins ∪ the server's current recent skill
  // runs (so a just-finished run shows instantly, before the pin effect commits
  // and makes it durable), deduped by runId, minus dismissed, minus anything
  // currently running again. Newest finish on top.
  const finishedByRunId = new Map<string, PinnedSkillRun>();
  for (const c of pinnedCards) finishedByRunId.set(c.runId, c);
  for (const t of recent) {
    if (t.kind === 'skill' && t.runId && !finishedByRunId.has(t.runId)) {
      finishedByRunId.set(t.runId, {
        runId: t.runId,
        kind: t.kind,
        name: t.name,
        sessionId: t.sessionId,
        skillSlug: t.skillSlug,
        startedAt: t.startedAt,
        finishedAt: t.finishedAt,
        isError: t.isError,
        cancelled: t.cancelled,
      });
    }
  }
  const finishedCards = [...finishedByRunId.values()]
    .filter((c) => !dismissedIds.has(c.runId))
    .filter((c) => !activeRunIds.has(c.runId))
    .sort((a, b) => b.finishedAt - a.finishedAt);

  // Compact strip: only coding/cron finishes. Skill runs are rich, persisted
  // cards above. Also drop anything still reported as active — the active +
  // recent reads aren't transactional, so a task can transiently land in both
  // (FRONT-745).
  const compactRecent = dedupeById(
    excludeActiveIds(
      recent.filter((t) => !(t.kind === 'skill' && t.runId)),
      active,
    ),
  );

  if (
    active.length === 0 &&
    finishedCards.length === 0 &&
    compactRecent.length === 0 &&
    bgVisible.length === 0
  ) {
    return <div className="text-xs text-muted-foreground/70 py-1">Nothing running.</div>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {bgVisible.map((c) => {
        const state = chatStates.get(c.sessionId) ?? 'working';
        return (
          <MonitoredChatRow
            key={`bgchat-${c.sessionId}`}
            chat={c}
            lightState={state}
            // A still-working chat has nothing to dismiss (use Stop if its run
            // is live). Everything else gets a dismiss — a settled receipt, OR
            // a stuck amber `needs-you`, which FRONT-786 lets the user clear:
            // the row cancels the dangling waiting prompt first, then hides.
            onDismiss={state === 'working' ? undefined : () => dismissPersistent(c.sessionId)}
          />
        );
      })}
      {active.map((t) => {
        if (t.kind === 'skill' && t.runId) return <SkillRunRow key={`active-${t.id}`} t={t} />;
        if (t.kind === 'coding' && t.sessionId) return <CodingRunRow key={`active-${t.id}`} t={t} />;
        if (t.kind === 'cron') return <CronRunRow key={`active-${t.id}`} t={t} />;
        return null;
      })}
      {finishedCards.map((c) => (
        <SkillRunRow
          key={`pinned-${c.runId}`}
          t={pinnedToTask(c)}
          settledIsError={c.isError}
          settledCancelled={c.cancelled}
          settledFinishedAt={c.finishedAt}
          onDismiss={() => dismissSkillRun(c.runId)}
        />
      ))}
      {/* The divider must separate "just finished" from EVERY row above it. */}
      {(bgVisible.length > 0 || active.length > 0 || finishedCards.length > 0) &&
        compactRecent.length > 0 && (
          <div className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/60 mt-1">
            Just finished
          </div>
        )}
      {compactRecent.slice(0, 5).map((t) => <RecentRow key={`recent-${t.id}`} t={t} />)}
    </div>
  );
}

/**
 * FRONT-634 follow-up — the chat-open / coding-open stores are only watched
 * by `home.tsx` / `chat-launcher.tsx`, both of which live on the home route.
 * The Inspector panel itself is mounted on almost every route
 * (`app-layout.tsx`), so a row clicked from, say, `/crons` used to just set a
 * request nobody was listening to yet — nothing visibly happened until the
 * user happened to navigate to home on their own, at which point the stale
 * request fired late. `isHomeRoute`/`originToReturnTo` now live in
 * `~/lib/chat-origin` (FRONT-1550) so the return-to-origin fix below can
 * share them without pulling this module's own queries into a lazy-loaded
 * route.
 *
 * A hook for "set the open request, then get the user to where something is
 * actually watching it" — shared by every row that opens a chat or coding
 * session, so the navigate-home guard lives in exactly one place. `open` is a
 * single, referentially-stable callback: both click and Enter/Space key
 * handlers call the SAME function, so there's no risk of divergent behaviour
 * (or of a request firing twice) between the two input paths.
 *
 * FRONT-1550: `open` now receives the origin path to return to on close (or
 * `null` when already home) so it can be threaded into the request store —
 * that's what lets closing the opened window come back here instead of
 * stranding the user on Home.
 */
function useOpenAndNavigateHome(open: (returnTo: string | null) => void): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    const returnTo = originToReturnTo(location);
    open(returnTo);
    if (returnTo !== null) navigate('/');
  }, [open, location, navigate]);
}

// FRONT-562: a running coding session. Clicking opens its window (the modal),
// via the coding-open store that home.tsx watches.
function CodingRunRow({ t }: { t: ActiveTask }) {
  const requestOpenCoding = useCodingOpenStore((s) => s.requestOpenCoding);
  const open = useOpenAndNavigateHome(
    useCallback(
      (returnTo) => {
        if (t.sessionId) requestOpenCoding(t.sessionId, returnTo);
      },
      [requestOpenCoding, t.sessionId],
    ),
  );
  return (
    <Tip label="Open the coding session">
      <button
        type="button"
        onClick={open}
        className="w-full text-left rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 flex items-center gap-2 hover:bg-amber-500/10 transition-colors"
      >
        <CircleNotch size={12} className="animate-spin text-amber-600 dark:text-amber-400 shrink-0" />
        <Code size={11} className="text-muted-foreground shrink-0" />
        <span className="text-xs font-mono text-foreground truncate flex-1">{t.name || 'Coding'}</span>
        <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">{relTime(t.startedAt)}</span>
      </button>
    </Tip>
  );
}

// FRONT-634: a running scheduled job. Display-only — no per-run action here
// (Stop/reauth live on the Crons page), just a link through to it.
function CronRunRow({ t }: { t: ActiveTask }) {
  return (
    <Link
      to="/crons"
      title="View scheduled jobs"
      className="w-full text-left rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 flex items-center gap-2 hover:bg-amber-500/10 transition-colors"
    >
      <CircleNotch size={12} className="animate-spin text-amber-600 dark:text-amber-400 shrink-0" />
      <Clock size={11} className="text-muted-foreground shrink-0" />
      <span className="text-xs font-mono text-foreground truncate flex-1">{t.name || 'Scheduled job'}</span>
      <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">{relTime(t.startedAt)}</span>
    </Link>
  );
}

/**
 * FRONT-634/754 — one row per monitored (backgrounded) ORDINARY chat: the
 * shared ChatLight status dot (driven by the chat-status spine, including
 * gray for a chat the user already opened and saw — this list does NOT
 * filter `done-seen` out the way the launcher's pill row does), the title, a
 * relative time, and a Stop or Dismiss button. Clicking the row itself
 * reopens the real conversation via `chat-open-store`. Under the launchpad
 * model this list never includes a chat whose only activity is a detached
 * deep research — that research is its own `SkillRunRow` card below.
 *
 * Stop vs. Dismiss are gated on two DIFFERENT things on purpose: Stop can only
 * ever cancel THIS row's own `background_chats` run, so it only shows while
 * that specific run is literally `running`. Dismiss instead reads the SPINE
 * state (`lightState`, passed down as `!!onDismiss`) — a session that's still
 * `working`/`needs-you` isn't something to dismiss yet, no matter what this
 * one row's own status says.
 */
function MonitoredChatRow({
  chat,
  lightState,
  onDismiss,
}: {
  chat: BackgroundChat;
  lightState: ChatLightState;
  onDismiss?: () => void;
}) {
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  const cancel = useCancelBackgroundChat();
  const dismissWaiting = useDismissChatWaiting();
  const canStopThisRun = chat.status === 'running';
  // FRONT-786: a `needs-you` (amber) row is waiting on a clarifying question.
  // Dismissing it must first clear that question server-side (so the light goes
  // gray everywhere), THEN hide the row. A terminal row just hides. `working`
  // rows never reach here — they get Stop, not dismiss. Since FRONT-715 a
  // typed-past question retires itself, so this is the "clear it now, without
  // sending anything" shortcut rather than the only way out.
  const isWaiting = lightState === 'needs-you';
  const startedMs = Date.parse(chat.startedAt);
  const handleDismiss = () => {
    if (isWaiting) {
      dismissWaiting.mutate(
        { sessionId: chat.sessionId },
        { onSuccess: () => onDismiss?.() },
      );
    } else {
      onDismiss?.();
    }
  };
  const open = useOpenAndNavigateHome(
    useCallback(
      (returnTo) => requestOpenChat(chat.sessionId, chat.title, returnTo),
      [requestOpenChat, chat.sessionId, chat.title],
    ),
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      className="rounded border border-border bg-card/40 px-2 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-muted/60 transition-colors"
    >
      <ChatLight state={lightState} size="size-2.5" />
      <ChatCircle size={11} className="text-muted-foreground shrink-0" />
      <Tip label={chat.title}>
        <span className="text-xs truncate flex-1 text-foreground font-medium">
          {chat.title}
        </span>
      </Tip>
      <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">
        {relTime(startedMs)}
      </span>
      {canStopThisRun ? (
        <Tip label="Stop background process">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              cancel.mutate({ runId: chat.runId });
            }}
            disabled={cancel.isPending}
            className="size-5 inline-flex items-center justify-center rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 shrink-0"
            aria-label="Stop"
          >
            <Stop size={10} weight="fill" />
          </button>
        </Tip>
      ) : (
        onDismiss && (
          <Tip label={
              isWaiting
                ? "Dismiss — clears this chat's 'waiting on you' prompt"
                : 'Dismiss'
            }>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              disabled={dismissWaiting.isPending}
              className="size-5 inline-flex items-center justify-center rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 shrink-0"
              aria-label={isWaiting ? 'Dismiss — clear the waiting prompt' : 'Dismiss'}
            >
              <X size={10} />
            </button>
          </Tip>
        )
      )}
    </div>
  );
}

/**
 * Skill runs are long-lived. We poll `/api/skill-status/:runId` so the card
 * can show the underlying Restate progress (turn N/M, last tool call) instead
 * of just "started Xs ago" — matches the upstream Background Tasks behaviour.
 * This is the launchpad model's home for a detached deep research: its own
 * card, its own progress, its own Stop — nothing left in the chat once it
 * detaches (see `chat-research-banner.tsx` for the in-chat notice, unchanged
 * from main).
 *
 * Progress-bar methodology (per PA's validation): never use `turn/maxTurns`
 * as a fill — max-turns is a budget cap, not a target, so the bar would
 * always look misleading. We render an indeterminate shimmer while running
 * and a solid 100 % fill on completion. `Turn N/M` is text on the detail row.
 *
 * A finished card is a "kept" card: it sticks around (and survives refreshes,
 * via the pinned-skill-runs store) until the user dismisses it with the ✕
 * button. `settledIsError` carries its authoritative success/failure so the
 * colour is right even once the live Restate status has aged out; it's
 * undefined for active (still-running) rows. `onDismiss` is supplied for kept
 * rows only — active rows aren't dismissable.
 */
// Exported for FRONT-2260's own render test (background-tasks.test.ts has no
// prior component-render coverage of this file — every existing test there
// exercises a pure helper only). Not otherwise used outside this module.
export function SkillRunRow({
  t,
  settledIsError,
  settledCancelled,
  settledFinishedAt,
  onDismiss,
}: {
  t: ActiveTask;
  settledIsError?: boolean;
  settledCancelled?: boolean;
  settledFinishedAt?: number;
  onDismiss?: () => void;
}) {
  const settled = settledIsError !== undefined;
  // FRONT-2260 fix round 1 (F2): a settled (kept) card still fetches its
  // status ONCE — that's the only way `status.model` (and the "Ran on"
  // line below) ever reaches a card that mounts already finished, which is
  // every kept card. It doesn't POLL (its terminal state is already known
  // via settledIsError, and the Restate status can eventually age out to
  // 'unknown', which would otherwise poll forever) — see
  // `skillRunStatusFetchPolicy`. Active rows still poll so the live
  // turn/tool detail stays fresh.
  const { data: status } = useSkillRunStatus(t.runId, { settled });
  const liveLabel = status?.status ?? 'starting';
  // A STOPPED run (FRONT-186) is its own terminal state — neither success nor
  // failure — so it gets a neutral "Stopped" card, never a green one.
  const isCancelled = settled ? settledCancelled === true : liveLabel === 'cancelled';
  const isCompleted =
    !isCancelled && (settled ? settledIsError === false : liveLabel === 'completed');
  const isFailed =
    !isCancelled && (settled ? settledIsError === true : liveLabel === 'failed');
  const isTerminal = isCompleted || isFailed || isCancelled;
  // FRONT-2260: "Ran on <model>" directly on the card — the result WINDOW
  // (SkillResultDialog, below) is unreachable for a plain custom skill run
  // (canViewResult requires a research briefing or the execute-plan
  // action-task), so this is the only place that run's model/fallback is
  // ever visible to a person. Same formatter, same query key (and thus the
  // same cache entry) as ModelCombobox and SkillResultDialog.
  const { data: modelsData } = useQuery({
    queryKey: llmKeys.models(),
    queryFn: () => api.get<ModelsResponse>('/api/llm/models'),
    staleTime: 5 * 60_000,
    enabled: isTerminal && !!status?.model,
  });
  const nameOfModel = (alias: string) => {
    const entry = modelsData?.models.find((m) => m.id === alias);
    return entry ? modelHeadline(entry) : alias;
  };
  const modelLine = isTerminal ? skillRunModelLine(status ?? {}, nameOfModel) : null;
  const turn = status?.currentTurn ?? 0;
  const max = status?.maxTurns ?? 0;
  const slug = status?.skillSlug ?? t.skillSlug ?? '';
  // Research-like runs get the topic + sources overlay: the dedicated
  // deep-research skill AND background plan runs (`plan-<id>`), which also
  // file a research_runs row + citations via the engine (ADR-0027).
  const isDeepResearch = slug === 'deep-research' || slug.startsWith('plan-');
  // FRONT-503: an action-task (the execute-plan acting route) produces no
  // research briefing — its result is the run's short `output` summary. A
  // completed one is now openable too (it used to be a dead green card), with
  // a deep link to the Schedule page where the scheduled task lands.
  const isActionTask = slug === 'execute-plan';

  // Research overlay: topic + sources counter. Each card resolves ITS OWN run
  // by this row's stable `runId` (the skillRunId), so every stacked, persistent
  // green card can open its result — not just the session's most recent one
  // (the old session-latest lookup left older cards un-clickable).
  const lookupRunId = isDeepResearch ? t.runId : undefined;
  const { data: run } = useResearchByRunId(lookupRunId);
  const matchingRun = run && run.skillRunId === t.runId ? run : null;
  // While the run is still working, poll citations so the source counter on the
  // running card climbs live — the engine files sources as it digs but emits no
  // per-source event, so without polling the count sits at 0 until completion.
  const { data: citations } = useResearchCitations(matchingRun?.id, {
    enabled: !!matchingRun,
    live: !!matchingRun && !isTerminal,
  });
  const sourceCount = citations?.length ?? 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  // Radix Dialog closes synchronously on pointer-down, then the mouseup/click
  // lands on whatever element is now under the cursor — which is THIS card,
  // whose onClick would immediately re-open the dialog. Guard with a short
  // window after every close so the X button and overlay-click actually
  // dismiss the dialog instead of bouncing.
  const recentlyClosedRef = useRef(false);
  const handleDialogOpenChange = useCallback((next: boolean) => {
    setDialogOpen(next);
    if (!next) {
      recentlyClosedRef.current = true;
      setTimeout(() => {
        recentlyClosedRef.current = false;
      }, 300);
    }
  }, []);
  const dismissPersistent = useDismissedSkillRuns((s) => s.dismiss);

  // A completed research/plan run delivers its result to the inbox AND (for
  // a grounded run on an attended session — FRONT-1500) into the chat that
  // started it; this card is a third place to see the same result. When a
  // briefing exists, the green card becomes a clickable receipt that opens
  // the result in a modal. A completed action-task has no briefing but
  // always carries an `output` summary, so it's openable too (FRONT-503).
  const canViewResult =
    isCompleted && (!!matchingRun?.briefingDocFilename || isActionTask);
  // FRONT-719: a *running* research/plan card is now clickable too — it opens a
  // live-progress view (phase + elapsed + sources + last error) so a stalled
  // run isn't an opaque frozen line. Needs the resolved research row for the
  // source count + the numeric id the Stop button cancels by.
  const canViewProgress = isDeepResearch && !isTerminal && !!matchingRun;
  const isClickable = isFailed || canViewResult || canViewProgress;
  // FRONT-719 / FRONT-122: keep the client-side elapsed timer ticking every
  // second while the run is live, independent of the 2–3s status poll (which
  // otherwise made it lurch, or freeze entirely when the poll went stale —
  // tab unfocused, focus-refetch off, or the poll stopped). The local tick is
  // what prevents the "frozen counter, still-spinning icon" bug. Stops once the
  // run settles. Do not remove without a replacement re-render source.
  useSecondTicker(!isTerminal);
  const cancelResearch = useCancelResearch();

  const containerClass = isFailed
    ? 'relative rounded border border-destructive/30 bg-destructive/5 px-2 py-2 flex flex-col gap-1.5 cursor-pointer hover:bg-destructive/10 transition-colors'
    : isCancelled
      ? 'relative rounded border border-muted-foreground/25 bg-muted/40 px-2 py-2 flex flex-col gap-1.5'
      : isCompleted
        ? `relative rounded border border-success/30 bg-success/5 px-2 py-2 flex flex-col gap-1.5${canViewResult ? ' cursor-pointer hover:bg-success/10 transition-colors' : ''}`
        : `relative rounded border border-amber-500/30 bg-amber-500/5 px-2 py-2 flex flex-col gap-1.5${canViewProgress ? ' cursor-pointer hover:bg-amber-500/10 transition-colors' : ''}`;

  const barClass = isFailed
    ? 'bg-destructive'
    : isCancelled
      ? 'bg-muted-foreground/50'
      : isCompleted
        ? 'bg-success'
        : 'bg-amber-500/70 animate-pulse';
  const barWidth = isTerminal ? '100%' : '30%';

  const TypeIcon = isDeepResearch ? MagnifyingGlass : Lightning;

  const interactiveProps = isClickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => {
          if (recentlyClosedRef.current) return;
          setDialogOpen(true);
        },
        onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDialogOpen(true);
          }
        },
      }
    : {};

  return (
    <div className={containerClass} data-testid="skill-run-card" data-run-id={t.runId} {...interactiveProps}>
      {/* Running research/plan → Stop (cancels the run, clears a stuck card).
          Kept/terminal → Dismiss. One or the other, top-right. */}
      {canViewProgress ? (
        <Tip label="Stop research">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              cancelResearch.mutate({ runId: matchingRun!.id });
            }}
            disabled={cancelResearch.isPending}
            className="absolute top-1 right-1 size-5 inline-flex items-center justify-center rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            aria-label="Stop"
          >
            <Stop size={10} weight="fill" />
          </button>
        </Tip>
      ) : (
        onDismiss && (
          <Tip label="Dismiss">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              className="absolute top-1 right-1 size-5 inline-flex items-center justify-center rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Dismiss"
            >
              <X size={10} />
            </button>
          </Tip>
        )
      )}
      <div className="flex items-center gap-2 pr-8">
        {isCompleted ? (
          <CheckCircle size={12} className="text-success shrink-0" weight="fill" />
        ) : isFailed ? (
          <XCircle size={12} className="text-destructive shrink-0" weight="fill" />
        ) : isCancelled ? (
          <Prohibit size={12} className="text-muted-foreground shrink-0" weight="fill" />
        ) : (
          <CircleNotch size={12} className="animate-spin text-amber-600 dark:text-amber-400 shrink-0" />
        )}
        <TypeIcon size={11} className="text-muted-foreground shrink-0" />
        {/* Research/plan AND action-task rows show a speaking headline (topic /
            plan title), not the raw "plan-55" / "execute-plan" slug. Other
            skills keep their mono slug. */}
        <Tip label={
            isDeepResearch
              ? matchingRun?.topic ?? t.name
              : isActionTask
                ? t.name
                : undefined
          }>
          <span
            className={`text-xs truncate flex-1 text-foreground ${
              isDeepResearch || isActionTask ? 'font-medium' : 'font-mono'
            }`}
          >
            {isDeepResearch
              ? matchingRun?.topic ?? t.name ?? slug
              : isActionTask
                ? t.name ?? slug
                : slug || t.name}
          </span>
        </Tip>
        <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">
          {settled && settledFinishedAt
            ? fmtDuration(settledFinishedAt - t.startedAt)
            : formatElapsed(t.startedAt)}
        </span>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full ${barClass} transition-all duration-300`}
          style={{ width: barWidth }}
        />
      </div>
      <div className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums truncate">
        {isTerminal ? (
          isCompleted ? (
            canViewResult ? 'Completed — click to view' : 'Completed'
          ) : isCancelled ? (
            'Stopped'
          ) : (
            <>Failed — click for details</>
          )
        ) : isDeepResearch ? (
          // Research/plan runs go through the engine, which has no conversation
          // "turns" — so show the live phase label (Planning → Researching →
          // Reviewing gaps → Writing) the engine reports, plus the climbing
          // source count, instead of a turn counter pinned at 0.
          <>
            {status?.lastToolCall || (liveLabel === 'starting' ? 'Starting…' : 'Researching…')}
            {matchingRun && (
              <> · {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}</>
            )}
          </>
        ) : (
          <>
            {max > 0 ? `Turn ${turn}/${max}` : liveLabel === 'starting' ? 'Starting…' : liveLabel}
            {status?.lastToolCall ? <> · {status.lastToolCall}</> : null}
          </>
        )}
      </div>
      {modelLine && (
        <div data-testid="skill-run-model" className="text-[length:var(--text-2xs)] text-muted-foreground">
          {modelLine}
        </div>
      )}
      {isFailed && t.runId && (
        <SkillFailureDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          runId={t.runId}
          sessionId={t.sessionId}
          skillSlug={slug}
          title={isActionTask ? t.name : undefined}
          startedAtFallback={t.startedAt}
          onDismiss={() => dismissPersistent(t.runId!)}
        />
      )}
      {canViewResult && t.runId && (
        <SkillResultDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          runId={t.runId}
          sessionId={t.sessionId}
          skillSlug={slug}
          title={t.name}
          startedAtFallback={t.startedAt}
        />
      )}
      {canViewProgress && t.runId && (
        <SkillProgressDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          runId={t.runId}
          title={matchingRun?.topic ?? t.name}
          startedAtFallback={t.startedAt}
        />
      )}
    </div>
  );
}

function RecentRow({ t }: { t: FinishedTask }) {
  const requestOpenCoding = useCodingOpenStore((s) => s.requestOpenCoding);
  const isCoding = t.kind === 'coding';
  const isCron = t.kind === 'cron';
  const open = useOpenAndNavigateHome(
    useCallback(
      (returnTo) => {
        if (isCoding && t.sessionId) requestOpenCoding(t.sessionId, returnTo);
      },
      [requestOpenCoding, isCoding, t.sessionId],
    ),
  );
  const Status = t.isError ? XCircle : CheckCircle;
  const Icon = isCron ? Clock : Code;
  const label = t.name || (isCron ? 'Scheduled job' : 'Coding');

  // FRONT-634: a finished cron/reflect run is display-only, linking through
  // to the Crons page rather than reopening anything in-place. (A finished
  // skill/research run never reaches this component at all — it's excluded
  // from `compactRecent` upstream and rendered as its own persistent
  // `SkillRunRow` card instead.)
  if (isCron) {
    return (
      <Link
        to="/crons"
        title="View scheduled jobs"
        className="rounded border border-border bg-card/40 px-2 py-1.5 flex items-center gap-2 hover:bg-muted/60 transition-colors"
      >
        <Status size={12} className={t.isError ? 'text-destructive shrink-0' : 'text-success shrink-0'} />
        <Icon size={11} className="text-muted-foreground shrink-0" />
        <span className="text-xs font-mono text-muted-foreground/80 truncate flex-1">{label}</span>
        <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">
          {fmtDuration(t.durationMs)}
        </span>
      </Link>
    );
  }

  const isClickable = isCoding && !!t.sessionId;
  const interactiveProps = isClickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: open,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        },
      }
    : {};
  const containerClass = isClickable
    ? 'rounded border border-border bg-card/40 px-2 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-muted/60 transition-colors'
    : 'rounded border border-border bg-card/40 px-2 py-1.5 flex items-center gap-2';
  return (
    <div className={containerClass} {...interactiveProps}>
      <Status size={12} className={t.isError ? 'text-destructive shrink-0' : 'text-success shrink-0'} />
      <Icon size={11} className="text-muted-foreground shrink-0" />
      <span className="text-xs font-mono text-muted-foreground/80 truncate flex-1">{label}</span>
      <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums shrink-0">
        {fmtDuration(t.durationMs)}
      </span>
    </div>
  );
}
